/**
 * CLRWorkflowPanel — self-contained WC quoting workflow panel.
 * Manages its own SSE stream, chat state (with localStorage persistence),
 * and field inputs. Props: runId, insuredName, onBack.
 *
 * Chat history is saved per runId in localStorage so "View Details"
 * on past runs restores the full AI conversation. For runs opened with
 * no history (direct link), the conversation is auto-reconstructed from
 * the SSE run state.
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  FiChevronLeft, FiCheck, FiAlertTriangle, FiArrowRight,
  FiRefreshCw as FiSpinner,
} from 'react-icons/fi';
import { RiSparklingFill } from 'react-icons/ri';
import { useRunStream, submitResume } from './useCommercialRating';
import PLRChatView from '../ratequotelink/PLRChatView';
import type { CLRunState, CLRunStep, PersistedChatMsg } from './useCommercialRating';
import '../../styles/RateQuoteLink.css';

// ─────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────

interface Props {
  runId: string;
  insuredName?: string;
  lob?: string;  // 'WC' | 'Cyber' | 'GL' | 'BOP' — defaults to 'WC'
  onBack: () => void;
}

// LOB display labels
const LOB_LABELS: Record<string, { full: string; short: string }> = {
  WC:    { full: "Workers' Comp", short: 'WC' },
  Cyber: { full: 'Cyber Policy',  short: 'Cyber' },
  GL:    { full: 'General Liability', short: 'GL' },
  BOP:   { full: 'Business Owners Policy', short: 'BOP' },
};

type ChatMsg = {
  id: string;
  role: 'ai' | 'user' | 'quote-cards';
  text: string;
  ts: number;
};

// ─────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────

function makeMsgId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function aiMsg(text: string, id?: string): ChatMsg {
  return { id: id ?? makeMsgId(), role: 'ai', text, ts: Date.now() };
}

function userMsg(text: string): ChatMsg {
  return { id: makeMsgId(), role: 'user', text, ts: Date.now() };
}

function quoteCardsMarker(): ChatMsg {
  return { id: 'quote-cards-marker', role: 'quote-cards', text: '', ts: Date.now() };
}

/** Reconstruct a basic chat history for runs already finished when opened. */
function reconstructHistory(
  runState: CLRunState,
  insuredName: string | undefined,
  seededSteps: Set<string>,
  lobFull: string = "Workers' Comp",
): ChatMsg[] {
  const msgs: ChatMsg[] = [];
  const label = insuredName ? ` for **${insuredName}**` : '';

  msgs.push(aiMsg(
    `${lobFull} quote${label} — viewing run history.`,
    'hist-start',
  ));

  runState.steps
    .filter(s => s.status === 'completed' && s.stepIndex >= 2)
    .forEach((step, i) => {
      const key = `step-done-${step.stepId}`;
      seededSteps.add(key);
      msgs.push(aiMsg(
        `**${step.stepLabel}** completed${step.durationMs ? ` in ${(step.durationMs / 1000).toFixed(1)}s` : ''}.`,
        `hist-step-${i}`,
      ));
    });

  if (runState.status === 'completed') {
    const allQuotes: any[] = runState.result?.quotes || [];
    const _isBridge = (q: any) => !!q.requiresBridging || q.quoteType === 'BRIDGE' || q.quoteType === 'bridge';
    const _isDeclined = (q: any) => q.statusCode === 'DECLINED' || q.statusCode === 'FAILED' || q.statusCode === 'DECLINE';
    const directQuoted = allQuotes.filter((q: any) => q.premiumAmount != null && q.premiumAmount > 0 && !_isBridge(q) && !_isDeclined(q));
    const bridgeQuoted = allQuotes.filter((q: any) => _isBridge(q) && q.premiumAmount != null && q.premiumAmount > 0);
    const bridgeNoPrice = allQuotes.filter((q: any) => _isBridge(q) && !q.premiumAmount);
    const quotedCount = directQuoted.length + bridgeQuoted.length;

    if (quotedCount > 0 || bridgeNoPrice.length > 0) {
      const best = directQuoted.length > 0
        ? directQuoted.reduce((a: any, b: any) => (a.premiumAmount < b.premiumAmount ? a : b))
        : bridgeQuoted.length > 0
          ? bridgeQuoted.reduce((a: any, b: any) => (a.premiumAmount < b.premiumAmount ? a : b))
          : null;
      const parts: string[] = [];
      if (directQuoted.length > 0)
        parts.push(`**${directQuoted.length}** direct quote${directQuoted.length !== 1 ? 's' : ''}`);
      if (bridgeQuoted.length > 0)
        parts.push(`**${bridgeQuoted.length}** bridge quote${bridgeQuoted.length !== 1 ? 's' : ''} with estimated premium`);
      if (bridgeNoPrice.length > 0)
        parts.push(`**${bridgeNoPrice.length}** carrier${bridgeNoPrice.length !== 1 ? 's' : ''} requiring portal completion`);
      const summary = parts.join(', ');
      const bestLine = best ? ` Best estimate: **$${best.premiumAmount?.toLocaleString()}** from **${best.carrierName || best.carrierId}**.` : '';
      msgs.push(aiMsg(
        `Quotes received! ${summary}.${bestLine} Full breakdown below.`,
        'hist-done',
      ));
    } else {
      msgs.push(aiMsg(
        `Workflow completed. ${runState.result?.summary || 'See results below.'}`,
        'hist-done',
      ));
    }
    // Insert quote-cards marker so Q&A chat appears below the cards
    msgs.push(quoteCardsMarker());
  } else {
    msgs.push(aiMsg(
      `Workflow ended with errors. ${runState.error || 'See step details for more information.'}`,
      'hist-fail',
    ));
  }

  return msgs;
}

// ─────────────────────────────────────────────────────────
// WCLoader — running state loader bubble
// ─────────────────────────────────────────────────────────

const WCLoader: React.FC<{ runState: CLRunState; lobShort?: string }> = ({ runState, lobShort = 'WC' }) => {
  const activeStep = runState.steps.find(s => s.status === 'running');
  const lastProgress = activeStep?.progress[activeStep.progress.length - 1];
  const completedCount = runState.steps.filter(s => s.status === 'completed').length;
  const totalCount = runState.steps.length;

  return (
    <div className="rql-chat__msg rql-chat__msg--ai rql-cql-row">
      <span className="rql-chat__avatar rql-chat__avatar--top">
        <RiSparklingFill size={11} />
      </span>
      <div className="rql-cql" style={{ flex: 1 }}>
        <div className="rql-cql__header">
          <div className="rql-cql__header-icon">
            <FiSpinner size={13} className="rql-row-execute__spin" />
          </div>
          <div className="rql-cql__header-text">
            <span className="rql-cql__title">Generating your {lobShort} quote</span>
            <span className="rql-cql__subtitle">AI agent working across carriers</span>
          </div>
        </div>
        <div className="rql-cql__track">
          {runState.steps.map((s, i) => (
            <div
              key={i}
              className={`rql-cql__seg${
                s.status === 'completed' ? ' rql-cql__seg--done' :
                s.status === 'running' ? ' rql-cql__seg--active' : ''
              }`}
            />
          ))}
        </div>
        {lastProgress ? (
          <div className="rql-cql__active-step">
            <div className="rql-cql__active-step-row">
              <span className="rql-cql__trail-icon rql-cql__trail-icon--running">
                <FiSpinner size={11} className="rql-row-execute__spin" />
              </span>
              <span className="rql-cql__active-step-label">{lastProgress.label}</span>
            </div>
            {lastProgress.detail && (
              <p className="rql-cql__active-step-detail">{lastProgress.detail}</p>
            )}
          </div>
        ) : activeStep ? (
          <p className="rql-cql__msg">{activeStep.stepLabel}</p>
        ) : (
          <p className="rql-cql__msg">Processing…</p>
        )}
        <p className="rql-cql__hint">
          {completedCount}/{totalCount} steps complete — results will appear automatically.
        </p>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────
// Quote Cards
// ─────────────────────────────────────────────────────────

const GoToRateButton: React.FC<{ runId?: string | null; rateUrl?: string | null }> = ({ runId, rateUrl }) => {
  const [url, setUrl] = React.useState<string | null>(rateUrl || null);
  const [loading, setLoading] = React.useState(false);
  const [notFound, setNotFound] = React.useState(false);

  const handleClick = async () => {
    if (url) {
      window.open(url, '_blank');
      return;
    }
    if (!runId || notFound) return;
    setLoading(true);
    try {
      const myampUrl = process.env.REACT_APP_MyAMP_Url || '';
      const token = localStorage.getItem('authToken') || '';
      const res = await fetch(`${myampUrl}/api/ams/commercial-rating/runs/${runId}/rate-url`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setUrl(data.rate_url);
        window.open(data.rate_url, '_blank');
      } else {
        setNotFound(true);
      }
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => { if (rateUrl) setUrl(rateUrl); }, [rateUrl]);

  if (notFound) return null;

  return (
    <button type="button" onClick={handleClick} disabled={loading}
       className="rql-icq__btn rql-icq__btn--rate"
       style={{
         display: 'inline-flex', alignItems: 'center', gap: 6,
         marginTop: 12, padding: '8px 16px', borderRadius: 8,
         background: '#381574', color: '#fff', fontSize: 13,
         fontWeight: 500, border: 'none', cursor: loading ? 'wait' : 'pointer',
       }}>
      <FiArrowRight size={12} /> {loading ? 'Loading...' : 'Go to Rate'}
    </button>
  );
};

const QuoteCards: React.FC<{ result: any; runId?: string }> = ({ result, runId: qcRunId }) => {
  const quotes: any[] = result?.quotes || [];

  // Determine per-carrier bridge flag — be lenient: quoteType "BRIDGE" or requiresBridging flag
  const isBridge = (q: any) =>
    !!q.requiresBridging || q.quoteType === 'BRIDGE' || q.quoteType === 'bridge';
  const isDeclined = (q: any) =>
    q.statusCode === 'DECLINED' || q.statusCode === 'FAILED' || q.statusCode === 'DECLINE';

  // Directly quoted (real premium, no bridging required)
  const quoted = [...quotes
    .filter((q: any) =>
      q.premiumAmount != null && q.premiumAmount > 0
      && !isBridge(q) && !isDeclined(q))]
    .sort((a, b) => (a.premiumAmount ?? Infinity) - (b.premiumAmount ?? Infinity));

  // Bridge quotes — any carrier flagged as bridge (with or without est. premium)
  // Sorted: bridge-with-premium first (cheapest first), then bridge-no-premium
  const bridged = [...quotes.filter((q: any) => isBridge(q) && !isDeclined(q))]
    .sort((a, b) => {
      if (a.premiumAmount && !b.premiumAmount) return -1;
      if (!a.premiumAmount && b.premiumAmount) return 1;
      return (a.premiumAmount ?? Infinity) - (b.premiumAmount ?? Infinity);
    });

  // Declined / failed — any carrier not quoted and not in bridge list
  const bridgedSet = new Set(bridged);
  const quotedSet  = new Set(quoted);
  const failed = quotes.filter((q: any) => !quotedSet.has(q) && !bridgedSet.has(q));

  const best = quoted[0] ?? null;
  const fmt$ = (n: number) => `$${Math.round(n).toLocaleString()}`;
  const monthly = (annual: number) => fmt$(Math.round(annual / 12));
  const initial = (q: any) => ((q.carrierName || q.carrierId || '?')[0] || '?').toUpperCase();
  const label = (q: any) => q.carrierName || q.carrierId || 'Unknown';

  const LogoOrBadge = ({ q }: { q: any }) => q.carrierLogoURL ? (
    <div className="rql-icq__logo-wrap">
      <img src={q.carrierLogoURL} className="rql-icq__logo" alt={label(q)} />
    </div>
  ) : (
    <span className="rql-icq__badge">{initial(q)}</span>
  );

  if (quoted.length === 0 && bridged.length === 0 && failed.length === 0) {
    return (
      <div className="rql-chat__msg rql-chat__msg--ai">
        <span className="rql-chat__avatar"><RiSparklingFill size={11} /></span>
        <div className="rql-icq__empty">No quote data returned.</div>
      </div>
    );
  }

  return (
    <div className="rql-chat__msg rql-chat__msg--ai rql-chat__msg--fullcard">
      <span className="rql-chat__avatar rql-chat__avatar--top">
        <RiSparklingFill size={11} />
      </span>
      <div className="rql-icq" style={{ flex: 1 }}>

        {/* ── Direct quotes ── */}
        {quoted.length > 0 && (
          <div className="rql-icq__cards">
            {quoted.map((q: any, qi: number) => (
              <div key={qi} className={`rql-icq__card${q === best ? ' rql-icq__card--best' : ''}`}>
                <div className="rql-icq__card-head">
                  <div className="rql-icq__card-ident">
                    <LogoOrBadge q={q} />
                    <div className="rql-icq__card-meta">
                      <div className="rql-icq__carrier">{label(q)}</div>
                      {q.grade && <div className="rql-icq__type">Grade: {q.grade}</div>}
                    </div>
                  </div>
                  {q === best && <span className="rql-icq__best"><FiCheck size={9} /> Best Price</span>}
                </div>
                <div className="rql-icq__pricing">
                  <div className="rql-icq__price-main">
                    <span className="rql-icq__amount">{fmt$(q.premiumAmount)}</span>
                    <span className="rql-icq__per">/ yr</span>
                  </div>
                  <div className="rql-icq__price-sep" />
                  <div className="rql-icq__price-sub">
                    <div className="rql-icq__price-sub-row">
                      <span className="rql-icq__monthly">{monthly(q.premiumAmount)}</span>
                      <span className="rql-icq__per">/ mo</span>
                    </div>
                  </div>
                </div>
                <div className="rql-icq__actions">
                  {q.bindLink ? (
                    <a href={q.bindLink} target="_blank" rel="noopener noreferrer" className="rql-icq__btn rql-icq__btn--bind">
                      <FiArrowRight size={11} /> Bind Now
                    </a>
                  ) : (
                    <span className="rql-icq__btn rql-icq__btn--bind" style={{ opacity: 0.45, cursor: 'not-allowed' }}>
                      <FiArrowRight size={11} /> Bind Now
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Bridge quotes ── */}
        {bridged.length > 0 && (
          <>
            <div className="rql-icq__cards" style={{ marginTop: quoted.length > 0 ? 14 : 0 }}>
              {bridged.map((q: any, qi: number) => (
                <div key={`b-${qi}`} className="rql-icq__card rql-icq__card--bridge">
                  <div className="rql-icq__card-head">
                    <div className="rql-icq__card-ident">
                      <LogoOrBadge q={q} />
                      <div className="rql-icq__card-meta">
                        <div className="rql-icq__carrier">{label(q)}</div>
                        <div className="rql-icq__type rql-icq__type--bridge">
                          {q.premiumAmount ? 'Estimated premium — finalize on portal' : 'Additional info required at portal'}
                        </div>
                      </div>
                    </div>
                    <span className="rql-icq__bridge-pill">Bridge</span>
                  </div>
                  {q.premiumAmount && (
                    <div className="rql-icq__pricing">
                      <div className="rql-icq__price-main">
                        <span className="rql-icq__amount rql-icq__amount--bridge">~{fmt$(q.premiumAmount)}</span>
                        <span className="rql-icq__per">/ yr est.</span>
                      </div>
                      <div className="rql-icq__price-sep" />
                      <div className="rql-icq__price-sub">
                        <div className="rql-icq__price-sub-row">
                          <span className="rql-icq__monthly">~{monthly(q.premiumAmount)}</span>
                          <span className="rql-icq__per">/ mo</span>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="rql-icq__actions">
                    {q.bindLink ? (
                      <a href={q.bindLink} target="_blank" rel="noopener noreferrer" className="rql-icq__btn rql-icq__btn--bind rql-icq__btn--bind-bridge">
                        <FiArrowRight size={11} /> Bridge
                      </a>
                    ) : (
                      <span className="rql-icq__btn rql-icq__btn--bind-disabled">No portal link</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* ── Declined / failed ── */}
        {failed.length > 0 && (
          <div className="rql-icq__failed" style={{ marginTop: (quoted.length > 0 || bridged.length > 0) ? 14 : 0 }}>
            <div className="rql-icq__failed-label">
              <FiAlertTriangle size={11} /> {failed.length} carrier{failed.length !== 1 ? 's' : ''} declined
            </div>
            <div className="rql-icq__failed-list">
              {failed.map((q: any, qi: number) => (
                <div key={qi} className="rql-icq__failed-row">
                  <div className="rql-icq__failed-badge">{initial(q)}</div>
                  <div className="rql-icq__failed-info">
                    <span className="rql-icq__failed-name">{label(q)}</span>
                    <span className="rql-icq__failed-reason" title={q.failureReason || ''}>
                      {q.failureReason
                        ? (q.failureReason.length > 90 ? q.failureReason.slice(0, 90) + '…' : q.failureReason)
                        : (q.statusCode || 'Declined')}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Go to Rate button — show for quoted/bridged/declined, hide for errors-only ── */}
        {(quoted.length > 0 || bridged.length > 0
          || failed.some((q: any) => q.statusCode === 'DECLINED' || q.statusCode === 'DECLINE')
        ) && (
          <GoToRateButton runId={qcRunId} rateUrl={result?.rate_url} />
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────
// FieldInputCard — AI asks user a question
// ─────────────────────────────────────────────────────────

const FieldInputCard: React.FC<{
  awaitingStep: CLRunStep;
  fieldValues: Record<string, string>;
  submitting: boolean;
  onChange: (name: string, value: string) => void;
  onSubmit: () => void;
}> = ({ awaitingStep, fieldValues, submitting, onChange, onSubmit }) => {
  const { fields } = awaitingStep.awaitingInput!;
  return (
    <div className="rql-chat__msg rql-chat__msg--ai rql-chat__msg--fullcard">
      <span className="rql-chat__avatar rql-chat__avatar--top">
        <RiSparklingFill size={11} />
      </span>
      <div className="rql-clr-field-card">
        <div className="rql-clr-field-card__fields">
          {(fields || []).map((f: any, fi: number) => {
            const fk = f.key || f.name || f.label || '';
            return (
            <div key={fk} className="rql-clr-field-card__row">
              <label className="rql-clr-field-card__label">
                {f.label}
                {f.required && <span className="rql-clr-field-card__req"> *</span>}
              </label>
              {f.type === 'select' ? (
                <select
                  className="rql-clr-field-card__input"
                  value={fieldValues[fk] || ''}
                  onChange={e => onChange(fk, e.target.value)}
                >
                  <option value="">Select…</option>
                  {(f.options || []).map((o: any) => (
                    <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>
                  ))}
                </select>
              ) : (
                <input
                  type={f.type === 'number' ? 'number' : 'text'}
                  className="rql-clr-field-card__input"
                  placeholder={f.placeholder || ''}
                  value={fieldValues[fk] || ''}
                  onChange={e => onChange(fk, e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && fi === fields.length - 1) onSubmit(); }}
                  autoFocus={fi === 0}
                />
              )}
            </div>
            );
          })}
        </div>
        <div className="rql-clr-field-card__footer">
          <span className="rql-clr-field-card__hint">Or type in the chat bar below</span>
          <button
            type="button"
            className="rql-iwf__submit"
            onClick={onSubmit}
            disabled={submitting}
          >
            <FiArrowRight size={12} /> {submitting ? 'Submitting…' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────
// StepTracker (left panel)
// ─────────────────────────────────────────────────────────

const StepTracker: React.FC<{ runState: CLRunState | null }> = ({ runState }) => (
  <div className="rql-iwf__stepper">
    <div className="rql-iwf__stepper-title">Workflow Steps</div>
    <AnimatePresence initial={false}>
      {!runState ? (
        // Skeleton — 6 shimmer placeholders
        Array.from({ length: 6 }, (_, si) => (
          <motion.div
            key={`skel-${si}`}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ delay: si * 0.05, duration: 0.25 }}
            className="rql-iwf__step rql-iwf__step--pending"
          >
            <div className="rql-iwf__step-rail">
              <div className="rql-iwf__step-dot"><span>{si + 1}</span></div>
              {si < 5 && <div className="rql-iwf__step-line" />}
            </div>
            <div className="rql-iwf__step-body">
              <div className="rql-iwf__step-head">
                <div className="rql-iwf__step-info">
                  <div className="rql-clr-skel-bar" style={{ height: 8, width: 32, marginBottom: 5 }} />
                  <div className="rql-clr-skel-bar" style={{ height: 11, width: 120 }} />
                </div>
              </div>
            </div>
          </motion.div>
        ))
      ) : (
        runState.steps.map((step, si) => (
          <motion.div
            key={step.stepId}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ delay: si * 0.04, duration: 0.25 }}
            className={`rql-iwf__step rql-iwf__step--${step.status}`}
          >
            <div className="rql-iwf__step-rail">
              <div className="rql-iwf__step-dot">
                {step.status === 'completed'      ? <FiCheck size={12} /> :
                 step.status === 'failed'         ? <FiAlertTriangle size={11} /> :
                 step.status === 'running'        ? <FiSpinner size={11} className="rql-row-execute__spin" /> :
                 step.status === 'awaiting_input' ? <RiSparklingFill size={11} /> :
                 <span>{si + 1}</span>}
              </div>
              {si < runState.steps.length - 1 && (
                <div className={`rql-iwf__step-line${step.status === 'completed' ? ' rql-iwf__step-line--completed' : ''}`} />
              )}
            </div>
            <div className="rql-iwf__step-body">
              <div className="rql-iwf__step-head">
                <div className="rql-iwf__step-info">
                  <span className="rql-iwf__step-eyebrow">Step {si + 1}</span>
                  <span className="rql-iwf__step-label">{step.stepLabel}</span>
                </div>
                <span className={`rql-iwf__step-pill rql-iwf__step-pill--${step.status}`}>
                  {step.status === 'completed'      ? 'Done' :
                   step.status === 'running'        ? 'In Progress' :
                   step.status === 'failed'         ? 'Failed' :
                   step.status === 'awaiting_input' ? 'Needs Input' :
                   'Pending'}
                </span>
              </div>
              {step.durationMs != null && step.status === 'completed' && (
                <div className="rql-iwf__step-desc">{(step.durationMs / 1000).toFixed(1)}s</div>
              )}
              {step.error && (
                <div className="rql-iwf__step-desc" style={{ color: '#ef4444' }}>{step.error}</div>
              )}
              {step.progress.length > 0 && (
                <div className="rql-iwf__substeps">
                  {step.progress.map((p, pi) => (
                    <div key={pi} className={`rql-iwf__substep rql-iwf__substep--${p.status}`}>
                      <span className="rql-iwf__substep-icon" />
                      <div className="rql-iwf__substep-content">
                        <span className="rql-iwf__substep-label">{p.label}</span>
                        {p.detail && <span className="rql-iwf__substep-detail">{p.detail}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        ))
      )}
    </AnimatePresence>
  </div>
);

// ─────────────────────────────────────────────────────────
// StatusBanner
// ─────────────────────────────────────────────────────────

const StatusBanner: React.FC<{ runState: CLRunState | null }> = ({ runState }) => {
  const status = runState?.status;
  return (
    <AnimatePresence>
      {status === 'completed' && (
        <motion.div key="ok" initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.22 }} className="rql-iwf__pause-banner rql-iwf__pause-banner--success">
          <FiCheck size={13} /><span className="rql-iwf__pause-banner-text">Quotes received</span>
        </motion.div>
      )}
      {status === 'failed' && (
        <motion.div key="fail" initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.22 }} className="rql-iwf__pause-banner">
          <FiAlertTriangle size={13} /><span className="rql-iwf__pause-banner-text">Workflow failed — {runState?.error || 'see step details'}</span>
        </motion.div>
      )}
      {status === 'awaiting_input' && (
        <motion.div key="await" initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.22 }} className="rql-iwf__pause-banner">
          <RiSparklingFill size={13} />
          <div>
            <span className="rql-iwf__pause-banner-text" style={{ fontWeight: 600 }}>Workflow paused — your input is required</span>
            <div style={{ fontSize: 11, opacity: 0.8, marginTop: 2 }}>The AI needs information from you before it can continue.</div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// ─────────────────────────────────────────────────────────
// ChatBubble — renders GitHub-flavoured Markdown
// ─────────────────────────────────────────────────────────

const ChatBubble: React.FC<{ text: string; isStreaming?: boolean }> = ({ text, isStreaming }) => (
  <div className="rql-chat__md">
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        a:          ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer" className="rql-chat__bubble-link">{children}</a>,
        p:          ({ children }) => <p className="rql-chat__md-p">{children}</p>,
        ul:         ({ children }) => <ul className="rql-chat__md-ul">{children}</ul>,
        ol:         ({ children }) => <ol className="rql-chat__md-ol">{children}</ol>,
        li:         ({ children }) => <li className="rql-chat__md-li">{children}</li>,
        strong:     ({ children }) => <strong className="rql-chat__md-strong">{children}</strong>,
        em:         ({ children }) => <em className="rql-chat__md-em">{children}</em>,
        h1:         ({ children }) => <div className="rql-chat__md-h1" style={{ fontSize: '12px', fontWeight: 700, margin: '10px 0 4px', lineHeight: 1.3 }}>{children}</div>,
        h2:         ({ children }) => <div className="rql-chat__md-h2" style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', margin: '8px 0 3px', lineHeight: 1.3 }}>{children}</div>,
        h3:         ({ children }) => <div className="rql-chat__md-h3" style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '6px 0 2px' }}>{children}</div>,
        hr:         () => <hr className="rql-chat__md-hr" />,
        blockquote: ({ children }) => <blockquote className="rql-chat__md-blockquote">{children}</blockquote>,
        table:      ({ children }) => <div className="rql-chat__md-table-wrap"><table className="rql-chat__md-table">{children}</table></div>,
        thead:      ({ children }) => <thead className="rql-chat__md-thead">{children}</thead>,
        tbody:      ({ children }) => <tbody className="rql-chat__md-tbody">{children}</tbody>,
        tr:         ({ children }) => <tr className="rql-chat__md-tr">{children}</tr>,
        th:         ({ children }) => <th className="rql-chat__md-th">{children}</th>,
        td:         ({ children }) => <td className="rql-chat__md-td">{children}</td>,
      }}
    >
      {text}
    </ReactMarkdown>
    {isStreaming && <span className="rql-chat__cursor" />}
  </div>
);

// ─────────────────────────────────────────────────────────
// CLRWorkflowPanel — main export
// ─────────────────────────────────────────────────────────

const RateMIcon: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9.63 13.16c0-1.55.01-3.1 0-4.65-.01-.83.3-1.52.84-2.11.94-1.02 1.92-2 2.83-3.03.46-.52 1.69-.47 2.01.31.06.14.1.3.1.45.01 3.08.01 6.15 0 9.23-.01.79-.38 1.46-.9 2.03-.98 1.08-1.99 2.13-3.01 3.17-.5.51-1.22.58-1.63.14-.16-.18-.23-.5-.24-.76-.02-1.59-.01-3.18-.01-4.77z" fill="currentColor"/>
    <path d="M21.91 8.78c0 1.5-.01 3-.01 4.5 0 .71-.23 1.33-.71 1.84-1.05 1.12-2.09 2.25-3.17 3.35-.23.24-.58.44-.9.49-.55.09-1.01-.4-1.01-1.02 0-3.17 0-6.34.02-9.5 0-.78.29-1.45.85-2.03.9-.94 1.78-1.9 2.65-2.87.42-.47.9-.68 1.52-.46.5.17.75.51.75 1.04v4.65z" fill="currentColor"/>
    <path d="M8.87 12.29c-.08 2.46.09 2-.4 3.45-.81.88-1.63 1.76-2.46 2.62-.22.23-.49.45-.78.56-.64.26-1.28-.19-1.29-.89-.01-.8-.04-1.59.02-2.38.02-.32.18-.69.4-.93 1.25-1.39 2.54-2.75 3.82-4.13.25-.27.51-.48.9-.46.45.02.71.29.72.75 0 .52 0 1.04 0 1.41z" fill="currentColor"/>
  </svg>
);

const CLRWorkflowPanel: React.FC<Props> = ({ runId, insuredName, lob = 'WC', onBack }) => {
  const lobLabel = LOB_LABELS[lob] || LOB_LABELS.WC;
  const isStarting = runId === '__starting__';
  const streamId = isStarting ? null : runId;
  const runState = useRunStream(streamId);

  // ── Chat & input state ──
  const [chatMessages, setChatMessages] = useState<ChatMsg[]>([]);
  const [thinking, setThinking] = useState(false);
  const [streamingMsgId, setStreamingMsgId] = useState<string | null>(null);
  const [chatInput, setChatInput] = useState('');
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [submittingInput, setSubmittingInput] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);
  // AI chat conversation history (for multi-turn context)
  const aiHistoryRef = useRef<Array<{ role: string; content: string }>>([]);

  // ── Seed tracking refs ──
  const prevStatusRef = useRef<string | null>(null);
  const seededStepsRef = useRef<Set<string>>(new Set());
  const loadedFromStorageRef = useRef(false);

  // Chat history is handled by PLRChatView via useChatStream + MongoDB chat_events

  const appendMsg = useCallback((msg: ChatMsg) => {
    setChatMessages(prev => [...prev, msg]);
  }, []);

  const updateMsg = useCallback((id: string, text: string) => {
    setChatMessages(prev => prev.map(m => m.id === id ? { ...m, text } : m));
  }, []);

  // Auto-scroll
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, thinking]);

  // ── Unified chat seeding / reconstruction ──
  useEffect(() => {
    if (!runState) return;
    const prev = prevStatusRef.current;
    const curr = runState.status;
    prevStatusRef.current = curr;

    // === FIRST ARRIVAL ===
    if (!prev) {
      if (loadedFromStorageRef.current) {
        // History loaded from storage — sync seeded-steps set to avoid duplicates
        runState.steps.forEach(s => seededStepsRef.current.add(`step-done-${s.stepId}`));
        // Re-inject quoteCardsMarker at the right slot (filtered out when saving to DB/LS)
        if (curr === 'completed') {
          setChatMessages(prev => {
            if (prev.some(m => m.role === 'quote-cards')) return prev;
            const firstUserIdx = prev.findIndex(m => m.role === 'user');
            const insertAt = firstUserIdx >= 0 ? firstUserIdx : prev.length;
            return [...prev.slice(0, insertAt), quoteCardsMarker(), ...prev.slice(insertAt)];
          });
        }
        return;
      }

      // No storage data
      if (curr === 'completed' || curr === 'failed') {
        // Opened from history — reconstruct from run state
        const msgs = reconstructHistory(runState, insuredName, seededStepsRef.current, lobLabel.full);
        setChatMessages(msgs);
        return;
      }

      if (curr === 'awaiting_input') {
        // Run is already paused when opened
        const awStep = runState.steps.find(s => s.status === 'awaiting_input' && s.awaitingInput);
        appendMsg(aiMsg(
          `${lobLabel.full} quote${insuredName ? ` for **${insuredName}**` : ''} — the AI needs your input to continue.`,
          'seed-start',
        ));
        if (awStep?.awaitingInput?.prompt) {
          appendMsg(aiMsg(awStep.awaitingInput.prompt, 'seed-prompt'));
        }
        return;
      }

      if (curr === 'running') {
        appendMsg(aiMsg(
          `Starting ${lobLabel.full} quote${insuredName ? ` for **${insuredName}**` : ''}. I'll work through each step automatically — no action needed from you yet.`,
          'seed-start',
        ));
      }
      return;
    }

    // === SUBSEQUENT TRANSITIONS (live run) ===

    // Step completions — seed for steps 3+
    runState.steps.forEach(step => {
      const key = `step-done-${step.stepId}`;
      if (step.status === 'completed' && !seededStepsRef.current.has(key) && step.stepIndex >= 2) {
        seededStepsRef.current.add(key);
        appendMsg(aiMsg(`**${step.stepLabel}** completed.`));
      }
    });

    // → awaiting_input
    if (curr === 'awaiting_input' && prev !== 'awaiting_input') {
      const awStep = runState.steps.find(s => s.status === 'awaiting_input' && s.awaitingInput);
      appendMsg(aiMsg(
        awStep?.awaitingInput?.prompt ||
        'I need some information from you before I can continue. Please fill in the form below.',
      ));
    }

    // → completed
    if (curr === 'completed' && prev !== 'completed') {
      const allQ: any[] = runState.result?.quotes || [];
      const directQ = allQ.filter((q: any) => q.premiumAmount != null && q.premiumAmount > 0 && !q.requiresBridging);
      const bridgeQ = allQ.filter((q: any) => q.requiresBridging && q.premiumAmount != null && q.premiumAmount > 0);
      const bridgeNone = allQ.filter((q: any) => q.requiresBridging && !q.premiumAmount);
      const total = directQ.length + bridgeQ.length + bridgeNone.length;

      if (total > 0) {
        const best = directQ.length > 0
          ? directQ.reduce((a: any, b: any) => (a.premiumAmount < b.premiumAmount ? a : b))
          : bridgeQ.length > 0
            ? bridgeQ.reduce((a: any, b: any) => (a.premiumAmount < b.premiumAmount ? a : b))
            : null;
        const parts: string[] = [];
        if (directQ.length > 0) parts.push(`**${directQ.length}** direct quote${directQ.length !== 1 ? 's' : ''}`);
        if (bridgeQ.length > 0) parts.push(`**${bridgeQ.length}** bridge quote${bridgeQ.length !== 1 ? 's' : ''} (est. premium)`);
        if (bridgeNone.length > 0) parts.push(`**${bridgeNone.length}** carrier${bridgeNone.length !== 1 ? 's' : ''} via portal`);
        const bestLine = best ? ` Best: **$${best.premiumAmount?.toLocaleString()}** from **${best.carrierName || best.carrierId}**.` : '';
        appendMsg(aiMsg(`Quotes are back! ${parts.join(', ')}.${bestLine} Full breakdown below.`));
      } else {
        appendMsg(aiMsg(`Workflow completed. ${runState.result?.summary || 'No premiums returned — see the results below.'}`));
      }
      // Insert quote-cards marker so Q&A chat appears BELOW the cards
      appendMsg(quoteCardsMarker());
    }

    // → failed
    if (curr === 'failed' && prev !== 'failed') {
      appendMsg(aiMsg(`Workflow failed: ${runState.error || 'An unexpected error occurred.'}`));
    }
  }, [runState?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Field input handlers ──
  const handleFieldChange = useCallback((name: string, value: string) => {
    setFieldValues(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmitInput = useCallback(async () => {
    if (!runState) return;
    const awaitingStep = runState.steps.find(s => s.status === 'awaiting_input' && s.awaitingInput);
    if (!awaitingStep) return;
    const fields = awaitingStep.awaitingInput?.fields || [];
    const userText = fields.map((f: any) => `${f.label}: ${fieldValues[f.key || f.name || f.label || ''] || ''}`).join('\n');
    appendMsg(userMsg(userText || 'Submitted'));
    setSubmittingInput(true);
    setThinking(true);
    try {
      await submitResume(runId, awaitingStep.stepId, fieldValues);
      appendMsg(aiMsg('Got it — continuing the workflow…'));
      setFieldValues({});
    } catch (err: any) {
      appendMsg(aiMsg(`Couldn't submit that — ${err?.message || 'please try again.'}`));
    } finally {
      setSubmittingInput(false);
      setThinking(false);
    }
  }, [runId, runState, fieldValues, appendMsg]);

  // ── Chat submit — dual mode: resume workflow OR Q&A streaming ──
  const handleChatSubmit = useCallback(async (preset?: string) => {
    const trimmed = (preset ?? chatInput).trim();
    if (!trimmed || !runState) return;

    const awaitingStep = runState.steps.find(s => s.status === 'awaiting_input' && s.awaitingInput);

    // ── Mode A: workflow is paused — resume it ──
    if (awaitingStep?.awaitingInput) {
      const fields = awaitingStep.awaitingInput.fields || [];
      const firstField = fields[0]?.key || fields[0]?.name || 'reply';
      appendMsg(userMsg(trimmed));
      setSubmittingInput(true);
      setThinking(true);
      try {
        await submitResume(runId, awaitingStep.stepId, { [firstField]: trimmed });
        appendMsg(aiMsg('Got it — continuing the workflow…'));
        setChatInput('');
        setFieldValues({});
      } catch (err: any) {
        appendMsg(aiMsg(`Couldn't submit that — ${err?.message || 'please try again.'}`));
      } finally {
        setSubmittingInput(false);
        setThinking(false);
      }
      return;
    }

    // ── Mode B: Q&A about the quote results — streaming ──
    const text = trimmed;
    appendMsg(userMsg(text));
    if (!preset) setChatInput('');
    setThinking(true);

    aiHistoryRef.current.push({ role: 'user', content: text });

    try {
      const authToken = localStorage.getItem('authToken') || '';

      const resp = await fetch(`${process.env.REACT_APP_MyAMP_Url}/api/ams/commercial-rating/runs/${runId}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({
          message: text,
          history: aiHistoryRef.current.slice(0, -1),
          insured_name: insuredName,
        }),
      });

      if (resp.ok && resp.body) {
        // Stream: create placeholder message, fill it chunk by chunk
        const msgId = makeMsgId();
        appendMsg({ id: msgId, role: 'ai', text: '', ts: Date.now() });
        setStreamingMsgId(msgId);
        setThinking(false);

        const reader = resp.body.getReader();
        const decoder = new TextDecoder();
        let full = '';

        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            const chunk = decoder.decode(value, { stream: true });
            full += chunk;
            updateMsg(msgId, full);
          }
        } finally {
          setStreamingMsgId(null);
        }

        if (full) {
          aiHistoryRef.current.push({ role: 'assistant', content: full });
        } else {
          updateMsg(msgId, 'I wasn\'t able to find an answer. Please try again.');
        }
      } else {
        setThinking(false);
        appendMsg(aiMsg('Connection issue — please try again.'));
      }
    } catch {
      setThinking(false);
      appendMsg(aiMsg('Something went wrong. Please try again.'));
    } finally {
      setThinking(false);
      setStreamingMsgId(null);
    }
  }, [chatInput, runId, runState, insuredName, appendMsg, updateMsg]);

  // ── Derived helpers ──
  const status = runState?.status ?? null;
  const isRunning = isStarting || !runState || status === 'running';
  const isAwaiting = status === 'awaiting_input';
  const isFailed = status === 'failed';
  const isCompleted = status === 'completed';
  const awaitingStep = runState?.steps.find(s => s.status === 'awaiting_input' && s.awaitingInput) ?? null;
  const firstField = awaitingStep?.awaitingInput?.fields?.[0];
  const completedCount = runState?.steps.filter(s => s.status === 'completed').length ?? 0;
  const totalCount = runState?.steps.length ?? 0;

  // Whether the quote-cards marker is already in the message list
  const hasQuoteCardsMarker = chatMessages.some(m => m.role === 'quote-cards');

  const chatPlaceholder = isRunning
    ? 'AI is working… (chat available when done)'
    : isAwaiting
      ? (firstField ? `Type your ${firstField.label.toLowerCase()}…` : 'Type your response…')
      : isCompleted
        ? 'Ask about results, carriers, premiums…'
        : 'Ask about this quote run…';
  const chatDisabled = isRunning || submittingInput || thinking || !!streamingMsgId;

  return (
    <div className="rql-iwf">

      {/* ── Top bar ── */}
      <div className="rql-iwf__head">
        <div className="rql-iwf__topbar">
          <div className="rql-iwf__topbar-left">
            <button type="button" className="rql-iwf__back" onClick={onBack}>
              <FiChevronLeft size={13} /> Back
            </button>
            {runState && (runState.status === 'completed' || runState.status === 'failed') && (
              <button type="button" className="rql-iwf__go-to-rate" onClick={async () => {
                try {
                  const tok = localStorage.getItem('authToken') || '';
                  const res = await fetch(`${process.env.REACT_APP_MyAMP_Url}/api/ams/commercial-rating/runs/${runId}/rate-url`, { headers: { Authorization: `Bearer ${tok}` } });
                  if (res.ok) { const d = await res.json(); if (d.rate_url) window.open(d.rate_url, '_blank'); }
                } catch { /* silently fail */ }
              }}>
                <RateMIcon size={14} /> Go to Rate
              </button>
            )}
          </div>
          <div className="rql-iwf__topbar-meta">
            {runState ? (
              <>
                <span className={`rql-iwf__status-dot rql-iwf__status-dot--${runState.status}`} />
                <span className="rql-iwf__run-label">{lobLabel.short} Quote</span>
                {insuredName && <><span className="rql-iwf__sep">·</span><span className="rql-iwf__run-label" style={{ opacity: 0.75 }}>{insuredName}</span></>}
                <span className="rql-iwf__sep">·</span>
                {runState.durationMs != null && (
                  <span className="rql-iwf__run-time">{(runState.durationMs / 1000).toFixed(1)}s</span>
                )}
                <span className="rql-iwf__step-count">{completedCount}/{totalCount} steps</span>
              </>
            ) : (
              <>
                <span className="rql-iwf__status-dot rql-iwf__status-dot--running" />
                <span className="rql-iwf__run-label">{lobLabel.short} Quote</span>
                {insuredName && <><span className="rql-iwf__sep">·</span><span className="rql-iwf__run-label" style={{ opacity: 0.75 }}>{insuredName}</span></>}
                <span className="rql-iwf__sep">·</span>
                <span className="rql-iwf__step-count">Starting…</span>
              </>
            )}
          </div>
        </div>
        <StatusBanner runState={runState} />
      </div>

      {/* ── Two-column body ── */}
      <div className="rql-iwf__scroll">
        <div className="rql-iwf__body">

          {/* LEFT: Step tracker */}
          <StepTracker runState={runState} />

          {/* RIGHT: Chat pane — uses shared PLRChatView */}
          <div className="rql-iwf__cpane rql-cpane">
            <PLRChatView
              runId={runId}
              insuredName={insuredName}
              runStatus={status || undefined}
              basePath="commercial-rating"
              onSubmitField={(rId, stepId, vals) => {
                submitResume(rId, stepId, vals);
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CLRWorkflowPanel;

